import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { EmployeeRole } from '../models/types';
import LoadingSpinner from '../components/common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: EmployeeRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, selectedRole } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If no specific roles are required, allow access
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has the required role
  const hasRequiredRole = selectedRole && allowedRoles.includes(selectedRole);
  
  if (!hasRequiredRole) {
    // Redirect based on role
    const roleRedirects: Record<EmployeeRole, string> = {
      [EmployeeRole.Admin]: '/admin/analytics',
      [EmployeeRole.LineSupervisor]: '/supervisor/analytics',
      [EmployeeRole.MachineOperator]: '/worker/tasks',
      [EmployeeRole.CleaningWorker]: '/worker/tasks',
      [EmployeeRole.NichromeOperators]: '/worker/tasks',
      [EmployeeRole.PackagingWorker]: '/worker/tasks',
    };

    const redirectPath = selectedRole ? roleRedirects[selectedRole] : '/analytics';
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 